import { useEffect, useState, type JSX } from 'react';
import { Button, LogoMarque, SettingsCode, SettingsInfo, SettingsMessage, SettingsRow, SettingsSection, Switch, type WhatsappAppairageView, type WhatsappPort } from '@taki/ui';

function heure(value: Date): string {
  return new Intl.DateTimeFormat('fr-LU', { timeStyle: 'short' }).format(value);
}

/**
 * LA SECTION « LIA SUR WHATSAPP » DE PARAMETRES > CONVERSATIONS (l'ex-onglet
 * IA) : connecter son numero a Lia (un code a envoyer, dix minutes), l'etat connecte (numero masque, deconnexion, son
 * propre interrupteur de notifications). LE CANAL DE L'ORG NE S'ALLUME PAS
 * ICI : un seul interrupteur, celui du moteur de notifications (Parametres >
 * Notifications, `canaux.whatsapp.actif`), decision du 05/10/2026
 * (`docs/socle/whatsapp-lia.md`).
 */
export function WhatsappSection({ port, aiEnabled }: { port: WhatsappPort | undefined; aiEnabled: boolean }): JSX.Element {
  const [appairage, setAppairage] = useState<WhatsappAppairageView | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => { if (port?.lien) setAppairage(null); }, [port?.lien?.phone]);

  const jouer = async (geste: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try { await geste(); }
    catch (cause) { setError(cause instanceof Error ? cause.message : String(cause)); }
    finally { setBusy(false); }
  };

  if (port === undefined) {
    return <SettingsSection title="Lia sur WhatsApp" contextLabel="Votre compte">
      <SettingsInfo>Indisponible dans cet environnement.</SettingsInfo>
    </SettingsSection>;
  }
  const lien = port.lien;
  const perime = appairage !== null && appairage.expiresAt.getTime() < Date.now();

  const aConnecter = appairage === null || perime;

  return <SettingsSection title="Lia sur WhatsApp" contextLabel="Votre compte">
    {lien === null
      ? <SettingsRow label="Mon numéro" hint={appairage === null
        ? undefined
        : perime ? 'Code expiré' : `Envoyer au ${port.numeroLiaLisible} avant ${heure(appairage.expiresAt)}.`}
        lock={aConnecter && !aiEnabled ? 'Activez Lia' : undefined}>
        {aConnecter
          ? <Button variant="dark" size="sm" loading={busy} disabled={!port.ready || !aiEnabled}
            onClick={() => void jouer(async () => { setAppairage(await port.appairer()); })}><LogoMarque marque="whatsapp" taille={16} />Connecter WhatsApp</Button>
          : <>
            <SettingsCode><span data-testid="whatsapp-code">{appairage.code}</span></SettingsCode>
            <a className="text-sm font-semibold text-ink-700 underline" href={appairage.lienWaMe} target="_blank" rel="noreferrer">Ouvrir WhatsApp avec le code</a>
          </>}
      </SettingsRow>
      : <>
        <SettingsRow label="Mon numéro" hint={`Lia : ${port.numeroLiaLisible}`}>
          <span className="text-sm text-ink-700" data-testid="whatsapp-numero">{lien.numeroMasque}</span>
          <Button variant="secondary" size="sm" loading={busy} title="Déconnecter, ou envoyer STOP à Lia" onClick={() => void jouer(() => port.delier())}>Déconnecter</Button>
        </SettingsRow>
        <SettingsRow label="Recevoir les notifications ici">
          <Switch checked={lien.notifications} locked={busy} aria-label="Recevoir les notifications sur WhatsApp"
            onChange={(value) => void jouer(() => port.setNotificationsPersonnelles(value))} />
        </SettingsRow>
      </>}
    {error === null ? null : <SettingsMessage tone="error">{error}</SettingsMessage>}
  </SettingsSection>;
}
